"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/shadcn/ui/input";
import { CornerButton } from "@/shadcn/ui/corner-button";

// Login schema
const loginSchema = z.object({
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
  password: z.string().min(6, "Das Passwort muss mindestens 6 Zeichen lang sein"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface SignInFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
}

export default function SignInForm({
  onSuccess,
  onForgotPassword,
}: SignInFormProps) {
  const { login, isLoading } = useAuth();

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    const response = await login(data.email, data.password);

    // If login was successful, call onSuccess callback
    if (response.success && onSuccess) {
      onSuccess();
    }
  };

  return (
    <form
      onSubmit={loginForm.handleSubmit(onSubmit)}
      className="space-y-8 text-black"
    >
      <div>
        <label
          htmlFor="email"
          className="block text-[1.75rem] text-black/80 mb-2"
        >
          E-Mail-Adresse
        </label>
        <Input
          type="email"
          id="email"
          {...loginForm.register("email")}
          placeholder="your@email.com"
          className={cn(
            "rounded-[.75rem] border border-black/20 placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 !text-black transition-colors",
            loginForm.formState.errors.email && "!border-red-500",
          )}
        />
        {loginForm.formState.errors.email && (
          <p className="mt-1 text-sm text-red-600 mb-0">
            {loginForm.formState.errors.email.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-[1.75rem] text-black/80 mb-2"
        >
          Passwort
        </label>
        <Input
          type="password"
          id="password"
          {...loginForm.register("password")}
          placeholder="Ihr Passwort"
          className={cn(
            "rounded-[.75rem] border border-black/20 placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 !text-black transition-colors",
            loginForm.formState.errors.password && "!border-red-500",
          )}
        />
        {loginForm.formState.errors.password && (
          <p className="mt-1 text-sm text-red-600 mb-0">
            {loginForm.formState.errors.password.message}
          </p>
        )}

        {onForgotPassword && (
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-black/50 underline text-[1.75rem] hover:text-black/80 transition-colors mt-1"
          >
            Passwort vergessen?
          </button>
        )}
      </div>

      <CornerButton
        className="[clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)] !bg-black w-full"
        type="dark"
        htmlType="submit"
        disabled={isLoading}
      >
        <div className="corner-button-text relative">
          <div className="corner-button-text-initial transition-all duration-300">
            {isLoading ? "Wird angemeldet…" : "Anmelden"}
          </div>
          <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
            {isLoading ? "Wird angemeldet…" : "Anmelden"}
          </div>
        </div>
      </CornerButton>
    </form>
  );
}
